import React from 'react';
import { useNotificationListState } from '../../../../shared/InAppNotificationsList/useNotificationListState';
import { useProfileInAppNotifications } from '../../../../../hooks/api/useProfileInAppNotifications';
import { SideDrawerButtonContainer } from '../../../Form/SideDrawerFormContainer';
import { getIntl } from '../../../../../utils/intl';
import InAppNotificationsList from '../../../../shared/InAppNotificationsList';
import { useNavigateOnInAppNotificationPress } from '../../../../shared/InAppNotificationCard';

const intl = getIntl(
    'components.screens.tabs.profiles.profileActivityScreen.desktopRecentActivitySection.messageSideDrawerContainer',
    {
        noMessages: 'No Profile Messages',
        messages: 'Messages',
        recentMessages: 'Recent Messages',
        profileMessages: 'Profile Messages',
        sendMessage: 'Send Message',
        sendProfileMessage: 'Send Profile Message',
        edit: 'Edit',
        markRead: 'Mark Read',
        markUnread: 'Mark Unread',
        markAllRead: 'Mark All Read',
        cancel: 'Cancel',
    },
);

export type MessageSideDrawerContentOwnProps = {
    onClose: () => void;
    medicalProfileId: string;
    onMessageProfile: () => void;
};

export type MessageSideDrawerContentProps = MessageSideDrawerContentOwnProps;

const MessageSideDrawerContent: React.FC<MessageSideDrawerContentProps> = ({
    onClose,
    medicalProfileId,
    onMessageProfile,
}) => {
    const navigateOnInAppNotificationOnPress = useNavigateOnInAppNotificationPress();
    const { isLoadingNotifications, allNotifications, toggleReadStatus, isTogglingReadStatus } =
        useProfileInAppNotifications({
            medicalProfileId,
        });
    const {
        getIsSelected,
        isSelecting,
        toggleIsSelecting,
        canMarkRead,
        canMarkUnread,
        markRead,
        markUnread,
        onToggleSelect,
        canMarkAllRead,
        onMarkAllRead,
        searchText,
        onSearchTextChange,
        onReadStatusFilterValueChange,
        readStatusFilterValue,
        hasAnyProfileNotifications,
        displayNotifications,
        sortOrder,
        onSortOrderChange,
    } = useNotificationListState({
        notifications: allNotifications,
        onPersistReadUnread: (notifications) => {
            toggleReadStatus({
                notificationsToToggle: notifications,
            });
        },
    });

    return (
        <SideDrawerButtonContainer
            disableScroll
            disablePadding
            buttons={[
                {
                    shouldShow: !isSelecting,
                    icon: 'Send',
                    testID: 'send-message-action',
                    title: intl('sendMessage'),
                    onPress: () => {
                        onClose();
                        onMessageProfile();
                    },
                    variant: 'primary',
                },
                {
                    shouldShow: !isSelecting,
                    icon: 'Edit',
                    testID: 'notifications-mark-read-unread-action',
                    title: intl('edit'),
                    onPress: toggleIsSelecting,
                    variant: 'primary',
                },
                {
                    shouldShow: isSelecting && canMarkAllRead,
                    icon: 'Check',
                    testID: 'notifications-mark-all-read-action',
                    title: intl('markAllRead'),
                    onPress: onMarkAllRead,
                    variant: 'primary',
                },
                {
                    shouldShow: isSelecting && canMarkRead,
                    icon: 'Check',
                    testID: 'notifications-mark-read-action',
                    title: intl('markRead'),
                    onPress: markRead,
                    variant: 'primary',
                },
                {
                    shouldShow: isSelecting && canMarkUnread,
                    icon: 'Check',
                    testID: 'notifications-mark-unread-action',
                    title: intl('markUnread'),
                    onPress: markUnread,
                    variant: 'primary',
                },
                {
                    shouldShow: isSelecting,
                    icon: 'X',
                    testID: 'notifications-cancel-action',
                    title: intl('cancel'),
                    onPress: toggleIsSelecting,
                    variant: 'secondary',
                },
            ]}
        >
            <InAppNotificationsList
                profileId={medicalProfileId}
                onNotificationPress={(...args) => {
                    navigateOnInAppNotificationOnPress(...args);
                    onClose();
                }}
                flat
                isSelectable={isSelecting}
                getIsSelected={getIsSelected}
                onToggleSelect={onToggleSelect}
                notifications={displayNotifications}
                isLoadingNotifications={isLoadingNotifications || isTogglingReadStatus}
                searchText={searchText}
                onSearchTextChange={onSearchTextChange}
                readStatusFilterValue={readStatusFilterValue}
                onReadStatusFilterValueChange={onReadStatusFilterValueChange}
                hasAnyProfileNotifications={hasAnyProfileNotifications}
                sortOrder={sortOrder}
                onSortOrderChange={onSortOrderChange}
                onMarkAsReadWhenNotificationPressed={(inAppNotifications) => {
                    toggleReadStatus({
                        notificationsToToggle: [inAppNotifications],
                        preventSuccessNotification: true,
                    });
                }}
            />
        </SideDrawerButtonContainer>
    );
};

export default React.memo(MessageSideDrawerContent);
